<template>
  <input
    autocomplete="off"
    :style="cellStyle"
    type="text"
    :data-row="dataoptions ? dataoptions['data-row'] : null"
    :data-col="dataoptions ? dataoptions['data-col'] : null"
    :data-fieldtype="dataoptions ? dataoptions['data-fieldtype'] : null"
    :data-fieldname="dataoptions ? dataoptions['data-fieldname'] : null"
    :ref="reference || null"
    :name="name"
    :required="required"
    :disabled="disabled"
    :readonly="disabled"
    :placeholder="placeholder"
    :tabindex="tabindex"
    :value="displayValue"
    @input="(e) => {
      displayValue = e.target.value
    }"
    @focus="handleFocus"
    @blur="handleBlur"
  />
</template>

<script>
  export default {
    props: {
      value: {
        'type': [ String, Number ],
        'required': false,
        'default': ''
      },
      name: {
        'type': String,
        'required': false,
        'default': ''
      },
      currency: {
        'type': String,
        'required': false,
        'default': ''
      },
      required: {
        'type': Boolean,
        'required': false,
        'default': false
      },
      decimals: {
        'type': Number,
        'required': false
      },
      disabled: {
        'type': Boolean,
        'required': false,
        'default': false
      },
      placeholder: {
        'type': String,
        'required': false,
        'default': ''
      },
      tabindex: {
        'type': String,
        'required': false,
        'default': ''
      },
      reference: {
        'type': String,
        'required': false,
        'default': ''
      },
      dataoptions: {
        'type': Object,
        'required': false
      },
      customStyle: {
        'type': Object,
        'required': false,
        'default': {}
      }
    },
    data () {
      return {
        isActive: false,
        hasCurrency: false,
        isNegative: false,
        hasError: false
      }
    },
    methods: {
      /**
       * Handles input focus, sets isActive flag to true and emits focus event
       * @param e   Focus event
       */
      handleFocus (e) {
        this.isActive = true
        this.$emit('focus', e)
      },
      /**
       * Handles blur event, sets isActive flag to false. If value is a valid number, parses float and emits formatted
       * value. Then emits blur event.
       * @param e   Focus event
       */
      handleBlur (e) {
        this.isActive = false
        if (this.value && !isNaN(parseFloat(this.value))) {
          this.$emit('input', parseFloat(this.value).toFixed(this.decimals || 2).toString())
        } else if (this.value && isNaN(parseFloat(this.value))) {
          this.$emit('input', '')
        }
        this.$emit('blur', e)
      },
      /**
       * Formats the current user input, allowing only currency number formats.
       * @param value   Current input value
       */
      formatRawInput (value = '') {
        if (typeof value !== 'string') {
          value = `${value}`
        }
        const isNegative = value[0] === '-'
        value = value
          .replace(/([^0-9,.])/g, '') // First, remove all letters and special chars
          .replace(/[,.](?=.*[,.])/g, '') // Next, replace all commas except for the last with an empty string
          .replace(/[,.]/g, '.') // Next, replace the last comma with a period
          .replace(/[.](?=.*[.])/g, '') // Finally, replace all periods except for the last with an empty string
        value = this.handleExcessZeroes(value)
        if (isNegative) {
          value = '-' + value
        }
        return value
      },
      /**
       * Handles cases where a string has leading or trailing zeroes
       * @param value   Current input value
       */
      handleExcessZeroes (value) {
        if (value.length === 1 && value[0] === '0') { // Allow first zero, since following chars might be allowable (i.e. '0.')
          return value
        }
        if (value.length === 1 && value[0] === '.') { // Handle cases where input is '.'
          value = '0.'
        }
        if (value.length > 1 && value[0] === '0' && value[1] !== '.') { // Remove leading zeroes if value is '0123...'
          value = value.replace(/^0+/, '') // Strip any leading zeroes
        }
        if (value.indexOf('.') > -1 && parseInt(value.substring(value.indexOf('.') + 1)) > 0) { // If value is a decimal, trim trailing zeroes
          value = parseFloat(value).toString()
        }
        return value
      },
      /**
       * Formats the user input to locale-based currency formats.
       */
      formatToLocale (value, sigDigits = 2, locale = 'en-US') {
        value = this.formatRawInput(value)
        if (value === 'NaN' || value === undefined) {
          return 'Error'
        }
        return Number(value).toLocaleString(locale, { minimumFractionDigits: sigDigits, maximumFractionDigits: sigDigits })
      }
    },
    computed: {
      displayValue: {
        /**
         * Gets the input value and displays the formatted value
         * Uses isActive data prop to check input field has focus, and un-formats the display value
         * If isActive is false, then the field isn't focused and the value should be formatted
         */
        get () {
          if (this.isActive) {
            this.hasError = false
            return this.value
          } else {
            return (this.value !== '') ? `${this.formatToLocale(this.value, this.decimals)}` : `${this.hasError ? 'Error' : ''}`
          }
        },
        /**
         * Recalculates value. If the value is negative, set isNegative flag to true and removes '-' from start of string
         * Otherwise, [re]sets isNegative to false
         * @param newValue
         */
        set (newValue) {
          this.$emit('input', this.formatRawInput(newValue))
        }
      },
      /**
       * Dynamically sets the style of this field depending on certain conditions such as whether it is disabled
       */
      cellStyle () {
        let style = {
          'box-sizing': 'border-box',
          'width': '100%',
          'padding-left': '14px;',
          ...this.customStyle
        }
        if (this.disabled) {
          style.cursor = 'not-allowed'
          style.background = '#eef1f6'
        } else if (this.required) {
          style['background-color'] = '#e2f0f5'
        } else {
          delete style.cursor
          style.background = '#fff'
        }
        return style
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/InputComponents/MoneyField.vue